import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown, Star, FileText, ClipboardList, MessageCircle, List, Share2, Heart, TrendingUp } from 'lucide-react';
interface ModuleProps {
  num: string;
  title: string;
  subtitle: string;
  exclusive?: boolean;
  items: string[];
  result: string;
  icon: React.ElementType;
  imgSrc: string;
}
function ModuleItem({
  num,
  title,
  subtitle,
  exclusive,
  items,
  result,
  icon: Icon,
  imgSrc
}: ModuleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const accentColor = exclusive ? 'violeta' : 'rosa';
  const bgAccentClass = exclusive ? 'bg-violeta' : 'bg-rosa';
  const textAccentClass = exclusive ? 'text-violeta' : 'text-rosa';
  const borderAccentClass = exclusive ? 'border-violeta' : 'border-rosa';
  const ringAccentClass = exclusive ? 'ring-violeta/20' : 'ring-rosa/20';
  const softBgClass = exclusive ? 'bg-violeta/5' : 'bg-rosa/5';
  return <div className={`relative bg-white rounded-xl border transition-all duration-300 overflow-hidden cursor-pointer
        ${isOpen ? `border-${accentColor} shadow-md ring-4 ${ringAccentClass}` : 'border-[#EAEAEA] hover:border-gray-300 hover:shadow-sm'}
      `} onClick={() => setIsOpen(!isOpen)} data-id="element-198">
      {/* Left accent bar for open state */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300 ${isOpen ? bgAccentClass : 'bg-transparent'}`} data-id="element-199" />

      <div className={`flex items-center gap-3 sm:gap-4 p-2.5 sm:p-4 transition-colors duration-300 ${isOpen ? softBgClass : ''}`} data-id="element-200">
        <div className="relative flex-shrink-0" data-id="element-201">
          <img src={imgSrc} alt={title} loading="lazy" className="w-10 h-10 sm:w-14 sm:h-14 object-cover rounded-lg shadow-sm" data-id="element-202" />
          <div className={`absolute -bottom-1.5 -right-1.5 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-white font-extrabold text-[9px] sm:text-[10px] shadow-sm border-2 border-white ${bgAccentClass}`} data-id="element-203">
            {num}
          </div>
        </div>

        <div className="flex-1 min-w-0 ml-1" data-id="element-204">
          <div className="flex items-center gap-1.5 mb-0.5" data-id="element-205">
            <Icon size={14} className={`${textAccentClass} flex-shrink-0 hidden sm:block`} data-id="element-206" />
            <strong className="block text-[13px] sm:text-[15px] font-bold text-oscuro truncate" data-id="element-207">
              {title}
            </strong>
          </div>
          {subtitle && <span className="text-[11px] sm:text-[13px] text-gris line-clamp-1 sm:line-clamp-none leading-tight" data-id="element-208">
              {subtitle}
            </span>}
          {exclusive && <span className="inline-block mt-1 text-[9px] sm:text-[11px] text-violeta font-bold bg-[#EEEDFE] px-2 py-0.5 rounded-md uppercase tracking-wider" data-id="element-209">
              ✦ Exclusivo Oferta Completa
            </span>}
        </div>

        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15
      }} className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? bgAccentClass + ' text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-210">
          <ChevronDown size={16} data-id="element-211" />
        </motion.div>
      </div>

      <AnimatePresence data-id="element-212">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} data-id="element-213">
            <div className={`px-4 sm:px-5 pb-4 sm:pb-5 pl-[56px] sm:pl-[84px] ${softBgClass}`} data-id="element-214">
              <ul className="flex flex-col gap-2 mb-4" data-id="element-215">
                {items.map((item, idx) => <li key={idx} className="text-[12px] sm:text-[13px] text-gris flex items-start gap-2.5 leading-[1.45]" data-id="element-216">
                    <ArrowRight size={14} className={`${textAccentClass} flex-shrink-0 mt-0.5`} data-id="element-217" />
                    <span data-id="element-218">{item}</span>
                  </li>)}
              </ul>
              <div className="p-3.5 bg-white rounded-lg text-[11px] sm:text-[12px] text-gris border-l-[3px] border-verde leading-relaxed shadow-sm" data-id="element-219">
                <strong className="text-oscuro" data-id="element-220">Resultado:</strong> {result}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Modules() {
  const modulesData = [{
    num: 'T1',
    title: 'Tu Marca Personal en la Estética',
    subtitle: 'La identidad que te hace diferente — y que justifica tus precios',
    icon: Star,
    imgSrc: 'https://images.unsplash.com/photo-1596462502278-27bf85033e5a?w=120&q=60&auto=format&fit=crop',
    items: ['Ejercicio de propuesta de valor única para tu servicio', 'Perfil completo de tu clienta ideal con todos sus datos relevantes', 'Guía de paleta de colores y estilo visual coherente', 'Fórmula para redactar tu bio profesional para Instagram y WhatsApp', 'Tips para elegir tu nombre comercial y slogan'],
    result: 'sabés exactamente quién sos, a quién le hablás y cómo querés que te vean — antes de terminar de leerlo.'
  }, {
    num: 'T2',
    title: 'Tus Presupuestos que Cierran Solos',
    subtitle: '3 plantillas PDF listas para enviar por WhatsApp en 10 minutos',
    icon: FileText,
    imgSrc: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=120&q=60&auto=format&fit=crop',
    items: ['Plantilla de presupuesto para servicio individual editable en Canva o Word', 'Plantilla de presupuesto para paquete de sesiones con ahorro destacado', 'Plantilla de propuesta especial para novias y eventos de alto valor', 'Fórmula para describir tratamientos que generan deseo antes del precio', 'Política de seña y condiciones de reserva lista para copiar'],
    result: 'el mismo día que descargás el kit podés mandar tu primer presupuesto profesional.'
  }, {
    num: 'T3',
    title: 'La Ficha de Clienta que Fideliza',
    subtitle: 'Registrá cada clienta como la profesional que ya sos',
    icon: ClipboardList,
    imgSrc: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=120&q=60&auto=format&fit=crop',
    items: ['Ficha de clienta completa: datos, historial de piel y objetivos', 'Formulario de contraindicaciones y antecedentes para cada tratamiento', 'Consentimiento informado listo para firmar o enviar digital', 'Tabla de seguimiento de sesiones por clienta', 'Nota de bienvenida personalizada para clientas nuevas'],
    result: 'tus clientas sienten desde la primera sesión que las conocés y las cuidás — y por eso vuelven.'
  }, {
    num: 'T4',
    title: 'Comunicación Profesional en Cada Turno',
    subtitle: 'Los 8 mensajes exactos para cada momento del vínculo con tu clienta',
    icon: MessageCircle,
    imgSrc: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=120&q=60&auto=format&fit=crop',
    items: ['Mensaje de respuesta a la primera consulta de precio', 'Confirmación de turno con política de seña incluida', 'Recordatorio 24hs antes — profesional y cercano', 'Mensaje post-sesión con cuidados y próximo paso', 'Guión para pedir reseña, reactivar clientas y manejar cancelaciones'],
    result: 'nunca más improvisa qué escribirle a una clienta — cada mensaje ya está pensado y redactado.'
  }, {
    num: 'T5',
    title: 'Tu Menú de Servicios y Precios',
    subtitle: 'Presentá lo que ofrecés de forma que el precio sea lo último que se cuestione',
    icon: List,
    imgSrc: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=120&q=60&auto=format&fit=crop',
    items: ['Plantilla de menú de tratamientos faciales con diseño premium', 'Plantilla de menú de tratamientos corporales', 'Plantilla de paquetes y combos con ahorro destacado', 'Diseño de voucher y tarjeta de regalo para fechas especiales', 'Calculadora de precio real para saber cuánto cobrar por cada tratamiento'],
    result: 'tu lista de precios deja de ser una planilla de texto y se convierte en una herramienta de venta.'
  }, {
    num: 'T6',
    title: 'Tu Plan de Marca Personal en Redes',
    subtitle: 'Estrategia simple y sostenible para crecer sin publicidad paga',
    icon: Share2,
    imgSrc: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=120&q=60&auto=format&fit=crop',
    items: ['Los 5 tipos de contenido que más convierten para esteticistas', 'Calendario mensual de publicaciones listo para completar', 'Banco de 30 ideas de contenido organizadas por tipo', 'Fórmula para el texto de cada posteo con 3 ejemplos completos', 'Guía de Historias de Instagram para llenar la agenda semana a semana'],
    result: 'sabés exactamente qué publicar cada semana sin improvisación ni estrés.'
  }, {
    num: 'T7',
    title: 'Sistema de Fidelización de Clientas',
    subtitle: '',
    exclusive: true,
    icon: Heart,
    imgSrc: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=120&q=60&auto=format&fit=crop',
    items: ['Sistema de clientas frecuentes por niveles (Bronce, Plata, Oro)', 'Tarjeta de beneficios por visita lista para imprimir y entregar', 'Sistema de seguimiento proactivo de la cartera en 15 min por semana', '5 mensajes de reactivación según el tiempo sin visita', 'Sistema de referidas y calendario de contacto anual por fechas clave'],
    result: 'esteticistas que implementaron este sistema pasaron de $280.000 a $390.000 mensuales con las mismas clientas en 3 meses.'
  }, {
    num: 'T8',
    title: 'Método de Aumento de Precios Sin Perder Clientas',
    subtitle: '',
    exclusive: true,
    icon: TrendingUp,
    imgSrc: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=120&q=60&auto=format&fit=crop',
    items: ['Calculadora de cuánto subir y en cuánto tiempo (método de 2 etapas)', '6 acciones para agregar valor percibido antes de comunicar el aumento', 'El mensaje exacto de aumento para cada tipo de clienta — listo para copiar', 'Respuesta lista para las 4 objeciones más frecuentes ("es caro", "antes era más barato"...)', 'Cronograma de 6 meses para llegar al precio ideal sin perder a nadie'],
    result: 'una esteticista de Rosario aplicó el método y pasó de $12.000 a $18.000 por limpieza. Con 18 turnos mensuales, eso representa $108.000 más por mes.'
  }];
  return <section className="bg-crema py-10 sm:py-16 px-5" data-id="element-221">
      <div className="max-w-[860px] mx-auto" data-id="element-222">
        <div className="text-center mb-8 sm:mb-12" data-id="element-223">
          <h2 className="text-[clamp(22px,4vw,34px)] font-extrabold leading-[1.2] mb-3 text-oscuro uppercase" data-id="element-224">
            CONTENIDO COMPLETO — LOS 8 TOMOS
          </h2>
          <p className="text-[15px] text-gris" data-id="element-225">
            Tu hoja de ruta para profesionalizar cada aspecto del negocio
            <br data-id="element-226" />
            <em className="text-[13px] not-italic opacity-80 mt-1 inline-block" data-id="element-227">
              Tocá cada tomo para ver lo que incluye 👆
            </em>
          </p>
        </div>

        <div className="flex flex-col gap-2.5 sm:gap-3" data-id="element-228">
          {modulesData.map((mod, idx) => <ModuleItem key={idx} {...mod} data-id="element-229" />)}
        </div>
      </div>
    </section>;
}